<template>
  <main class="pricing-page">
    <section class="pricing-shell">
      <nav class="tabs"><button class="active">가격 책정 치료비 계획</button></nav>

      <div class="toolbar">
        <label class="patient-picker">
          <span>환자</span>
          <input v-model="patientQuery" placeholder="이름/차트/전화 검색" @focus="patientListOpen = true" />
          <div v-if="patientListOpen && patientQuery.trim()" class="patient-results">
            <button v-for="patient in filteredPatients" :key="patient.chartNo" type="button" @click="selectPatient(patient)">
              <strong>{{ patient.name }}</strong>
              <span>{{ patient.chartNo }} · {{ patient.phone || '-' }}</span>
            </button>
          </div>
        </label>
        <button @click="newPlan">새 계획</button>
        <button @click="savePlan">저장</button>
        <button class="primary" @click="submitPlan">제출</button>
        <button :disabled="!selectedPlan" @click="deletePlan">계획 삭제</button>
      </div>

      <section class="summary-grid">
        <article><span>환자</span><strong>{{ currentPatient?.name || '미선택' }}</strong></article>
        <article><span>치료비 합계</span><strong>{{ money(subtotal) }}</strong></article>
        <article><span>부가세</span><strong>{{ money(vat) }}</strong></article>
        <article><span>총 계약금액</span><strong>{{ money(total) }}</strong></article>
      </section>

      <section class="content-grid">
        <aside class="side-panel">
          <section>
            <header>저장된 치료비 계획</header>
            <button
              v-for="plan in patientPlans"
              :key="plan.id"
              type="button"
              :class="{ selected: selectedPlanId === plan.id }"
              @click="loadPlan(plan)"
            >
              <strong>{{ plan.title }}</strong>
              <span>{{ plan.date }} · {{ money(plan.total) }}</span>
            </button>
            <p v-if="!patientPlans.length">저장된 계획이 없습니다.</p>
          </section>

          <section>
            <header>치료 항목 카탈로그</header>
            <button v-for="catalog in catalogItems" :key="catalog.name" type="button" @click="addCatalogItem(catalog)">
              <strong>{{ catalog.name }}</strong>
              <span>{{ money(catalog.price) }}</span>
            </button>
          </section>
        </aside>

        <section class="plan-panel">
          <header>
            <strong>치료 계획 작성</strong>
            <label><span>제목</span><input v-model="editing.title" /></label>
          </header>

          <div class="add-row">
            <input v-model="customItem.name" placeholder="처치 내용" />
            <input v-model.number="customItem.price" type="number" min="0" placeholder="금액" />
            <button @click="addCustomItem">추가</button>
          </div>

          <div class="table-area">
            <table>
              <thead><tr><th>치식/부위</th><th>처치 내용</th><th>금액</th><th>메모</th><th>삭제</th></tr></thead>
              <tbody>
                <tr v-for="item in editing.items" :key="item.id">
                  <td><input v-model="item.tooth" placeholder="#36" /></td>
                  <td><input v-model="item.name" /></td>
                  <td><input v-model.number="item.price" type="number" min="0" /></td>
                  <td><input v-model="item.memo" /></td>
                  <td><button class="danger" @click="removeItem(item.id)">삭제</button></td>
                </tr>
              </tbody>
            </table>
            <p v-if="!editing.items.length">치료 항목을 추가하세요.</p>
          </div>

          <footer>
            <label><span>담당의사</span><select v-model="editing.doctor"><option>김원장</option><option>이원장</option><option>박원장</option></select></label>
            <label><span>상담자</span><select v-model="editing.counselor"><option>데스크</option><option>김실장</option><option>이실장</option></select></label>
            <label class="memo"><span>상담 메모</span><textarea v-model="editing.memo" rows="3" /></label>
          </footer>
        </section>
      </section>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

type Patient = { id?: number; chartNo: string; name: string; phone?: string };
type PlanItem = { id: number; tooth: string; name: string; price: number; memo: string };
type TreatmentPlan = { id: number; chartNo: string; patientName: string; date: string; title: string; items: PlanItem[]; subtotal: number; vat: number; total: number; doctor: string; counselor: string; memo: string; status: '작성' | '제출' };

const STORAGE_KEY = 'bioa-treatment-plans';
const router = useRouter();
const patients = ref<Patient[]>(loadPatients());
const plans = ref<TreatmentPlan[]>(readStorage<TreatmentPlan[]>(STORAGE_KEY, []));
const currentPatient = ref<Patient | null>(patients.value[0] || null);
const patientQuery = ref(currentPatient.value ? `${currentPatient.value.name} ${currentPatient.value.chartNo}` : '');
const patientListOpen = ref(false);
const selectedPlanId = ref<number | null>(null);
const customItem = reactive({ name: '', price: 0 });
const editing = reactive<TreatmentPlan>(blankPlan());

const catalogItems: Array<{ name: string; price: number; tooth: string }> = [];

const filteredPatients = computed(() => {
  const q = patientQuery.value.trim().toLowerCase();
  return patients.value.filter((p) => [p.name, p.chartNo, p.phone].join(' ').toLowerCase().includes(q)).slice(0, 40);
});
const patientPlans = computed(() => plans.value.filter((plan) => !currentPatient.value || plan.chartNo === currentPatient.value.chartNo).sort((a, b) => b.id - a.id));
const selectedPlan = computed(() => plans.value.find((plan) => plan.id === selectedPlanId.value) || null);
const subtotal = computed(() => editing.items.reduce((sum, item) => sum + Number(item.price || 0), 0));
const vat = computed(() => Math.round(subtotal.value * 0.1));
const total = computed(() => subtotal.value + vat.value);

function selectPatient(patient: Patient) {
  currentPatient.value = patient;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
  newPlan();
}

function blankPlan(): TreatmentPlan {
  return {
    id: 0,
    chartNo: currentPatient.value?.chartNo || '',
    patientName: currentPatient.value?.name || '',
    date: today(),
    title: '치료비 계획',
    items: [],
    subtotal: 0,
    vat: 0,
    total: 0,
    doctor: '김원장',
    counselor: '데스크',
    memo: '',
    status: '작성',
  };
}

function newPlan() {
  selectedPlanId.value = null;
  Object.assign(editing, blankPlan());
}

function loadPlan(plan: TreatmentPlan) {
  selectedPlanId.value = plan.id;
  Object.assign(editing, clonePlan(plan));
}

function addCatalogItem(item: { name: string; price: number; tooth: string }) {
  editing.items.push({ id: Date.now() + Math.random(), tooth: item.tooth, name: item.name, price: item.price, memo: '' });
}

function addCustomItem() {
  if (!customItem.name.trim()) return;
  editing.items.push({ id: Date.now() + Math.random(), tooth: '', name: customItem.name, price: Number(customItem.price || 0), memo: '' });
  customItem.name = '';
  customItem.price = 0;
}

function removeItem(id: number) {
  editing.items = editing.items.filter((item) => item.id !== id);
}

function savePlan(status: '작성' | '제출' = '작성') {
  if (!currentPatient.value) return alert('환자를 선택하세요.');
  if (!editing.items.length) return alert('치료 항목을 추가하세요.');
  const payload: TreatmentPlan = {
    ...clonePlan(editing),
    id: selectedPlanId.value || Date.now(),
    chartNo: currentPatient.value.chartNo,
    patientName: currentPatient.value.name,
    date: today(),
    subtotal: subtotal.value,
    vat: vat.value,
    total: total.value,
    status,
  };
  const index = plans.value.findIndex((plan) => plan.id === payload.id);
  if (index >= 0) plans.value[index] = payload;
  else plans.value.push(payload);
  selectedPlanId.value = payload.id;
  Object.assign(editing, clonePlan(payload));
  persist();
  return payload;
}

function submitPlan() {
  const saved = savePlan('제출');
  if (!saved) return;
  router.push({ path: '/plan', query: { chartNo: saved.chartNo, planId: String(saved.id) } });
}

function deletePlan() {
  if (!selectedPlan.value || !window.confirm('선택한 치료비 계획을 삭제할까요?')) return;
  plans.value = plans.value.filter((plan) => plan.id !== selectedPlanId.value);
  persist();
  newPlan();
}

function clonePlan(plan: TreatmentPlan): TreatmentPlan {
  return { ...plan, items: plan.items.map((item) => ({ ...item, price: Number(item.price || 0) })) };
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans.value));
}

function money(value: number) {
  return `${Math.round(value || 0).toLocaleString('ko-KR')}원`;
}

function loadPatients(): Patient[] {
  const stored = readStorage<Patient[]>('bioa-patients', []);
  if (stored.length) return stored.map((p) => ({ chartNo: p.chartNo || `P-${p.id || 'TEMP'}`, name: p.name || '환자', phone: p.phone || '' }));
  return [];
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

if (currentPatient.value) newPlan();
</script>

<style scoped>
.pricing-page{height:100%;min-height:0;overflow:hidden;background:#f6f9fb;color:#34404a;font-size:13px}.pricing-shell{height:100%;min-height:0;background:#fff;border-top:1px solid #d9e2ea;display:flex;flex-direction:column;overflow:hidden}.tabs{display:flex;gap:22px;height:38px;align-items:flex-end;padding:0 20px;border-bottom:1px solid #d9e2ea;flex:0 0 auto}.tabs button{height:38px;border:0;background:transparent;color:#2275d7;font-weight:900;position:relative}.tabs button:after{content:'';position:absolute;left:8px;right:8px;bottom:-1px;height:2px;background:#2275d7}.toolbar{height:52px;display:flex;align-items:center;gap:6px;padding:8px 16px;border-bottom:1px solid #e3eaf0;box-sizing:border-box}.toolbar button,.side-panel button,.plan-panel button{height:30px;border:0;border-radius:4px;background:#e8eef4;color:#3d4b57;padding:0 12px;font-weight:800;cursor:pointer}.toolbar button:disabled{opacity:.45}.toolbar .primary{background:#2f7bea;color:#fff}.patient-picker{position:relative;display:grid;grid-template-columns:auto 260px;align-items:center;gap:6px;color:#7b8994}.patient-picker input{height:30px;border:1px solid #cfd9e3;border-radius:4px;padding:0 8px}.patient-results{position:absolute;top:34px;left:36px;width:340px;max-height:240px;overflow:auto;border:1px solid #cfd9e3;background:#fff;box-shadow:0 14px 30px rgba(35,49,61,.16);z-index:5;padding:5px}.patient-results button{width:100%;height:auto;min-height:40px;background:#fff;text-align:left;display:grid;grid-template-columns:1fr auto}.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;padding:12px 16px;border-bottom:1px solid #e3eaf0;background:#fbfcfd}.summary-grid article{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px}.summary-grid span{display:block;color:#7b8994}.summary-grid strong{display:block;margin-top:4px;font-size:17px;color:#263746}.content-grid{min-height:0;flex:1;display:grid;grid-template-columns:300px minmax(0,1fr);overflow:hidden}.side-panel{min-height:0;border-right:1px solid #e3eaf0;background:#fbfcfd;padding:12px;box-sizing:border-box;overflow:auto;display:grid;align-content:start;gap:12px}.side-panel section{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px;display:grid;gap:7px}.side-panel header,.plan-panel header strong{font-weight:900;color:#263746}.side-panel button{height:auto;min-height:42px;background:#fff;border:1px solid #e3eaf0;text-align:left;display:grid;gap:2px}.side-panel button:hover,.side-panel button.selected{border-color:#2f7bea;background:#eef6ff}.side-panel span{color:#7b8994}.side-panel p,.table-area p{margin:0;padding:12px;color:#8a97a3}.plan-panel{min-height:0;display:flex;flex-direction:column;overflow:hidden}.plan-panel header{height:48px;border-bottom:1px solid #e3eaf0;display:flex;align-items:center;gap:12px;padding:0 16px}.plan-panel header label{display:grid;grid-template-columns:auto 260px;align-items:center;gap:6px;margin-left:auto}.plan-panel input,.plan-panel select,.plan-panel textarea{border:1px solid #cfd9e3;border-radius:4px;box-sizing:border-box}.plan-panel input,.plan-panel select{height:30px;padding:0 8px}.add-row{height:48px;display:grid;grid-template-columns:minmax(0,1fr) 140px 74px;gap:8px;padding:9px 16px;border-bottom:1px solid #e3eaf0;box-sizing:border-box}.table-area{min-height:0;flex:1;overflow:auto}.table-area table{width:100%;border-collapse:collapse;table-layout:fixed}.table-area th,.table-area td{height:34px;border-bottom:1px solid #e5ebf0;border-right:1px solid #edf2f6;padding:4px;text-align:left}.table-area th{position:sticky;top:0;background:#f8fafc;color:#7b8994}.table-area input{width:100%}.danger{background:#ffe7e2!important;color:#b43a2d!important}.plan-panel footer{border-top:1px solid #d9e2ea;padding:12px 16px;display:grid;grid-template-columns:220px 220px minmax(0,1fr);gap:10px}.plan-panel footer label{display:grid;gap:5px;color:#5f6e7b}.memo textarea{width:100%;resize:vertical;padding:8px}
</style>
